import React, { useState, useEffect, Suspense, lazy } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from './i18n';
import './App.css';
import Header from './components/Header';
import Footer from './components/Footer';
import MainScreen from './components/MainScreen';
import GameScreen from './components/GameScreen';
import CookieConsent from './components/CookieConsent';
import Advertisement from './components/Advertisement';
import FeedbackBubble from './components/FeedbackBubble';
import { ThemeProvider, ToastProvider, AdProvider } from './context';
import { fetchCurrentUser, fetchRandomBackgroundImage, clearApiCache } from './services/apiService';
import { connectSocket, disconnectSocket } from './services/socketService';
import { trackPageView } from './utils/matomoTracker';

// Lazy-loaded components (code splitting for better initial load performance)
const ScoresScreen = lazy(() => import('./components/ScoresScreen'));
const UserConfigScreen = lazy(() => import('./components/UserConfigScreen'));
const AdminLayout = lazy(() => import('./components/admin/AdminLayout'));
const AdminDashboardScreen = lazy(() => import('./components/AdminDashboardScreen'));
const AdminScreen = lazy(() => import('./components/AdminScreen'));
const AdminMapsScreen = lazy(() => import('./components/AdminMapsScreen'));
const AdminGamemodesScreen = lazy(() => import('./components/AdminGamemodesScreen'));
const AdminUsersScreen = lazy(() => import('./components/AdminUsersScreen'));
const AdminSoundsScreen = lazy(() => import('./components/AdminSoundsScreen'));
const AdminRolesScreen = lazy(() => import('./components/AdminRolesScreen'));
const AdminReportsScreen = lazy(() => import('./components/AdminReportsScreen'));
const AdminHeatmapScreen = lazy(() => import('./components/AdminHeatmapScreen'));
const AdminSeasonsScreen = lazy(() => import('./components/AdminSeasonsScreen'));
const ProfileScreen = lazy(() => import('./components/ProfileScreen'));
const PublicProfileScreen = lazy(() => import('./components/PublicProfileScreen'));
const MultiplayerScreen = lazy(() => import('./components/MultiplayerScreen'));
const MultiplayerLobby = lazy(() => import('./components/MultiplayerLobby'));
const MultiplayerGame = lazy(() => import('./components/MultiplayerGame'));
const MultiplayerResults = lazy(() => import('./components/MultiplayerResults'));
const MultiplayerLeaderboard = lazy(() => import('./components/MultiplayerLeaderboard'));
const MatchmakingQueue = lazy(() => import('./components/MatchmakingQueue'));
const RankedEndScreen = lazy(() => import('./components/RankedEndScreen'));
const OverlayStreamPage = lazy(() => import('./components/OverlayStreamPage'));

// Loading fallback component
const LoadingFallback = () => (
  <div className="loading-fallback">
    <div className="loading-spinner"></div>
    <p>{i18n.t('common.loading', 'Loading...')}</p>
  </div>
);

function App() {
  const { t } = useTranslation();
  const [currentScreen, setCurrentScreen] = useState('main'); // 'main', 'game', 'scores', 'config', 'admin-dashboard', 'admin-points', 'admin-maps', 'admin-gamemodes', 'admin-users', 'admin-sounds'
  const [user, setUser] = useState(null);
  const [backgroundImage, setBackgroundImage] = useState('');
  const [globalMuted, setGlobalMuted] = useState(false);
  const [globalVolume, setGlobalVolume] = useState(25);
  const [gameState, setGameState] = useState({
    currentRound: 0,
    scores: [],
    difficulty: 'medium',
    gamemode: null,
    sessionId: null
  });
  const [publicProfileIdentifier, setPublicProfileIdentifier] = useState(null);
  const [multiplayerState, setMultiplayerState] = useState({
    phase: 'menu',  // 'menu' | 'join' | 'lobby' | 'game' | 'results' | 'leaderboard'
    lobbyId: null,
    lobbyCode: null,
    isHost: false,
    roundData: null,
    maps: [],
    leaderboard: null,
    initialRoundData: null,
    autoJoinCode: null
  });
  const [rankedState, setRankedState] = useState({
    phase: 'idle', // 'idle' | 'queue' | 'lobby' | 'game' | 'results' | 'leaderboard'
    lobbyId: null,
    lobbyCode: null,
    opponent: null,
    rankedYou: null,
    roundData: null,
    maps: [],
    leaderboard: null,
    initialRoundData: null,
    initialPlayers: [],
    rankedResult: null
  });
  const [pendingInvite, setPendingInvite] = useState(null);

  // Global listener for lobby invitations via socket
  useEffect(() => {
    if (!user) return;

    const socket = connectSocket();

    const onInviteReceived = (data) => {
      setPendingInvite(data);
      // Auto-dismiss after 15 seconds
      setTimeout(() => setPendingInvite(prev => prev === data ? null : prev), 15000);
    };

    const onRankedResult = (data) => {
      setRankedState(prev => ({ ...prev, rankedResult: data }));
    };

    socket.on('friend:invite-received', onInviteReceived);
    socket.on('ranked:result', onRankedResult);

    return () => {
      socket.off('friend:invite-received', onInviteReceived);
      socket.off('ranked:result', onRankedResult);
    };
  }, [user]);

  const handleAcceptInvite = (lobbyCode) => {
    setPendingInvite(null);
    showMultiplayer(lobbyCode);
  };

  const handleDismissInvite = () => {
    setPendingInvite(null);
  };

  // Check if user is logged in on component mount
  useEffect(() => {
    const checkUser = async () => {
      try {
        const userData = await fetchCurrentUser();
        if (userData && !userData.error) {
          setUser(userData);
        }
      } catch (error) {
        console.error('Error fetching user:', error);
      }
    };

    checkUser();
  }, []);

  // Set random background image on component mount
  useEffect(() => {
    const loadBackgroundImage = async () => {
      try {
        const imagePath = await fetchRandomBackgroundImage();
        setBackgroundImage(imagePath);
      } catch (error) {
        console.error('Error loading background image:', error);
        setBackgroundImage(''); // Set empty string if there's an error
      }
    };

    loadBackgroundImage();
  }, []);

  // Handle URL-based routing on mount
  useEffect(() => {
    const handleUrlRouting = () => {
      const path = window.location.pathname;

      // Overlay pages — standalone, no app shell
      if (path.startsWith('/overlay/')) {
        setCurrentScreen('overlay-stream');
        return;
      }

      // Check for /profile/:identifier route
      const profileMatch = path.match(/^\/profile\/(.+)$/);
      if (profileMatch) {
        const identifier = decodeURIComponent(profileMatch[1]);
        setPublicProfileIdentifier(identifier);
        setCurrentScreen('public-profile');
        return;
      }

      // Check for /scores route
      if (path === '/scores') {
        setCurrentScreen('scores');
        return;
      }

      // Check for /config route
      if (path === '/config') {
        setCurrentScreen('config');
        return;
      }

      // Check for /profile route (own profile, no identifier)
      if (path === '/profile') {
        setCurrentScreen('profile');
        return;
      }

      // Check for /admin route
      if (path === '/admin') {
        setCurrentScreen('admin-dashboard');
        return;
      }

      // Check for /multiplayer route with code parameter
      if (path === '/multiplayer' || path.startsWith('/multiplayer')) {
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        setCurrentScreen('multiplayer');
        if (code) {
          // Store the code so MultiplayerScreen can auto-fill it
          setMultiplayerState(prev => ({ ...prev, phase: 'join', autoJoinCode: code }));
        }
        return;
      }

    };

    handleUrlRouting();

    // Handle browser back/forward navigation
    const handlePopState = () => {
      const path = window.location.pathname;
      if (path === '/' || path === '') {
        setCurrentScreen('main');
        setPublicProfileIdentifier(null);
      } else {
        handleUrlRouting();
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Track page views when screen changes
  useEffect(() => {
    // Map screen names to more readable page names for analytics
    const pageNames = {
      main: 'Home',
      game: 'Game',
      scores: 'Leaderboard',
      config: 'User Settings',
      'public-profile': 'Public Profile',
      'admin-dashboard': 'Admin Dashboard',
      'admin-points': 'Admin Points',
      'admin-maps': 'Admin Maps',
      'admin-gamemodes': 'Admin Gamemodes',
      'admin-users': 'Admin Users',
      'admin-sounds': 'Admin Sounds',
      'admin-roles': 'Admin Roles',
      'admin-seasons': 'Admin Seasons',
      multiplayer: 'Multiplayer',
      ranked: 'Ranked'
    };

    // Track the page view with the appropriate name
    trackPageView(pageNames[currentScreen] || currentScreen);
  }, [currentScreen]);

  // Function to start a new game
  const startGame = (difficulty, gamemode) => {
    // Clear API cache to ensure fresh data when starting a new game
    clearApiCache();

    setGameState(prevState => ({
      ...prevState,
      currentRound: 1,
      scores: [],
      difficulty: difficulty || 'medium',
      gamemode: gamemode, // Add gamemode to the game state
      sessionId: null,
      challengeId: null,
      dailyChallengeId: null,
      dailyRoundCount: null
    }));
    setCurrentScreen('game');
  };

  // Function to start a daily challenge game
  // Rounds are fetched one at a time from the server via /api/challenge?dailyChallengeId=X
  // If resumeFrom > 1, the user is resuming an in-progress challenge
  const startDailyChallenge = (challengeData) => {
    clearApiCache();

    const resumeRound = challengeData.resumeFrom || 1;
    const savedScores = challengeData.savedScores || [];

    setGameState(prevState => ({
      ...prevState,
      currentRound: resumeRound,
      scores: savedScores.map(s => s.score || 0),
      difficulty: challengeData.difficulty || 'medium',
      gamemode: null,
      sessionId: null,
      challengeId: null,
      dailyChallengeId: challengeData.id,
      dailyRoundCount: challengeData.roundCount || 5
    }));
    setCurrentScreen('game');
  };

  // Function to show scores screen
  const showScores = () => {
    setCurrentScreen('scores');
  };

  // Function to return to main screen
  const returnToMainScreen = () => {
    // Clear API cache to ensure fresh data when returning to main screen
    clearApiCache();

    // Reset game state when returning to main screen
    setGameState(prevState => ({
      ...prevState,
      currentRound: 0,
      scores: [],
      sessionId: null,
      challengeId: null,
      dailyChallengeId: null,
      dailyRoundCount: null
    }));
    setCurrentScreen('main');
  };

  // Function to show user configuration screen
  const showUserConfig = () => {
    setCurrentScreen('config');
  };

  // Function to show admin dashboard (entry point for admin)
  const showAdmin = () => {
    setCurrentScreen('admin-dashboard');
  };

  // Function to show profile screen
  const showProfile = () => {
    setCurrentScreen('profile');
  };

  // Function to show public profile screen
  const showPublicProfile = (identifier) => {
    setPublicProfileIdentifier(identifier);
    setCurrentScreen('public-profile');
    window.history.pushState({}, '', `/profile/${encodeURIComponent(identifier)}`);
  };

  // Function to close public profile and return to main
  const closePublicProfile = () => {
    setPublicProfileIdentifier(null);
    setCurrentScreen('main');
    window.history.pushState({}, '', '/');
  };

  // Helper to check if current screen is an admin screen
  const isAdminScreen = currentScreen.startsWith('admin-');


  // --- Multiplayer flow handlers ---

  // Called from MainScreen or Header, optionally with an invite code
  const showMultiplayer = (code) => {
    setCurrentScreen('multiplayer');
    if (code && typeof code === 'string') {
      setMultiplayerState(prev => ({ ...prev, phase: 'join', autoJoinCode: code }));
    } else {
      setMultiplayerState(prev => ({ ...prev, phase: 'menu', autoJoinCode: null }));
    }
  };

  // Called when lobby is created/joined
  const handleLobbyJoined = (lobbyData) => {
    setMultiplayerState(prev => ({
      ...prev,
      phase: 'lobby',
      lobbyId: lobbyData.lobbyId,
      lobbyCode: lobbyData.code,
      isHost: lobbyData.isHost,
      initialPlayers: lobbyData.players || [],
      initialSettings: lobbyData.settings || {},
      autoJoinCode: null
    }));
  };

  // Called when game starts (round:start received)
  const handleMPGameStart = (gameData) => {
    setMultiplayerState(prev => ({
      ...prev,
      phase: 'game',
      initialRoundData: gameData,
      maps: gameData.maps || []
    }));
  };

  // Called when round ends
  const handleMPRoundEnd = (resultData) => {
    setMultiplayerState(prev => ({
      ...prev,
      phase: 'results',
      roundData: resultData
    }));
  };

  // Called when next round starts
  const handleMPNextRound = (roundData) => {
    setMultiplayerState(prev => ({
      ...prev,
      phase: 'game',
      initialRoundData: roundData || prev.initialRoundData
    }));
  };

  // Called when game ends — if still viewing results, store leaderboard without switching phase
  const handleMPGameEnd = (leaderboardData) => {
    setMultiplayerState(prev => {
      if (prev.phase === 'results') {
        // Final round: store leaderboard, let user view round results first
        return { ...prev, leaderboard: leaderboardData };
      }
      return { ...prev, phase: 'leaderboard', leaderboard: leaderboardData };
    });
  };

  // Called from MultiplayerResults to transition to leaderboard after viewing final round results
  const handleMPShowLeaderboard = () => {
    setMultiplayerState(prev => ({
      ...prev,
      phase: 'leaderboard'
    }));
  };

  // Called on rematch — receives data from lobby:rematch-started event
  const handleMPRematch = (data) => {
    setMultiplayerState(prev => ({
      ...prev,
      phase: 'lobby',
      roundData: null,
      leaderboard: null,
      initialRoundData: null,
      initialPlayers: (data && data.players) || prev.initialPlayers
    }));
  };

  // Called on exit from multiplayer
  const exitMultiplayer = () => {
    disconnectSocket();
    setMultiplayerState({
      phase: 'menu',
      lobbyId: null,
      lobbyCode: null,
      isHost: false,
      roundData: null,
      maps: [],
      leaderboard: null,
      initialRoundData: null,
      initialPlayers: [],
      autoJoinCode: null
    });
    setCurrentScreen('main');
  };

  // --- Ranked flow handlers ---

  const showRanked = () => {
    setCurrentScreen('ranked');
    setRankedState(prev => ({ ...prev, phase: 'queue', rankedResult: null }));
  };

  const handleRankedMatchFound = (data) => {
    setRankedState(prev => ({
      ...prev,
      phase: 'lobby',
      lobbyId: data.lobbyId,
      lobbyCode: data.code,
      opponent: data.opponent,
      rankedYou: data.you || null,
      initialPlayers: []
    }));
  };

  const handleRankedCancel = () => {
    setRankedState({
      phase: 'idle', lobbyId: null, lobbyCode: null, opponent: null, rankedYou: null,
      roundData: null, maps: [], leaderboard: null, initialRoundData: null,
      initialPlayers: [], rankedResult: null
    });
    setCurrentScreen('main');
  };

  const handleRankedGameStart = (gameData) => {
    setRankedState(prev => ({
      ...prev,
      phase: 'game',
      initialRoundData: gameData,
      maps: gameData.maps || []
    }));
  };

  const handleRankedRoundEnd = (resultData) => {
    setRankedState(prev => ({
      ...prev,
      phase: 'results',
      roundData: resultData
    }));
  };

  const handleRankedNextRound = (roundData) => {
    setRankedState(prev => ({
      ...prev,
      phase: 'game',
      initialRoundData: roundData || prev.initialRoundData
    }));
  };

  const handleRankedGameEnd = (leaderboardData) => {
    setRankedState(prev => {
      if (prev.phase === 'results') {
        return { ...prev, leaderboard: leaderboardData };
      }
      return { ...prev, phase: 'leaderboard', leaderboard: leaderboardData };
    });
  };

  const handleRankedShowLeaderboard = () => {
    setRankedState(prev => ({
      ...prev,
      phase: 'leaderboard'
    }));
  };

  const exitRanked = () => {
    disconnectSocket();
    setRankedState({
      phase: 'idle', lobbyId: null, lobbyCode: null, opponent: null, rankedYou: null,
      roundData: null, maps: [], leaderboard: null, initialRoundData: null,
      initialPlayers: [], rankedResult: null
    });
    setCurrentScreen('main');
  };

  // Function to update game state
  const updateGameState = (newState) => {
    setGameState(prevState => ({
      ...prevState,
      ...newState
    }));
  };

  // Overlay screens render standalone (no Header, Footer, or app wrapper)
  if (currentScreen === 'overlay-stream') {
    return (
      <Suspense fallback={null}>
        <OverlayStreamPage />
      </Suspense>
    );
  }

  return (
    <ThemeProvider>
      <ToastProvider>
        <AdProvider user={user}>
          <div
            className="App"
          style={{
            ...(backgroundImage && backgroundImage.startsWith('#')
              ? { backgroundColor: backgroundImage } // Use as background color
              : {
                  backgroundImage: backgroundImage ? `url(${backgroundImage})` : 'none',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  backgroundAttachment: 'fixed'
                }
            )
          }}
        >
          <Header
            user={user}
            onShowScores={showScores}
            onReturnToMain={returnToMainScreen}
            onShowUserConfig={showUserConfig}
            onShowAdmin={showAdmin}
            onShowProfile={showProfile}
            onShowMultiplayer={showMultiplayer}
            onShowPublicProfile={showPublicProfile}
          />

          {currentScreen === 'main' && (
            <MainScreen
              onStartGame={startGame}
              onStartDaily={startDailyChallenge}
              user={user}
              onShowMultiplayer={showMultiplayer}
              onShowPublicProfile={showPublicProfile}
            />
          )}

          {currentScreen === 'game' && (
            <GameScreen
              gameState={gameState}
              updateGameState={updateGameState}
              onGameEnd={returnToMainScreen}
              user={user}
              globalMuted={globalMuted}
              setGlobalMuted={setGlobalMuted}
              globalVolume={globalVolume}
              setGlobalVolume={setGlobalVolume}
            />
          )}

          <Suspense fallback={<LoadingFallback />}>
            {currentScreen === 'scores' && (
              <ScoresScreen
                user={user}
                onClose={returnToMainScreen}
                onShowPublicProfile={showPublicProfile}
              />
            )}

            {currentScreen === 'config' && (
              <UserConfigScreen
                user={user}
                onClose={returnToMainScreen}
                onUserUpdate={setUser}
                globalMuted={globalMuted}
                globalVolume={globalVolume}
                setGlobalVolume={setGlobalVolume}
              />
            )}

            {currentScreen === 'profile' && (
              <ProfileScreen
                user={user}
                onClose={returnToMainScreen}
                onUserUpdate={setUser}
              />
            )}

            {currentScreen === 'public-profile' && (
              <PublicProfileScreen
                identifier={publicProfileIdentifier}
                onClose={closePublicProfile}
              />
            )}

            {currentScreen === 'multiplayer' && (
              <>
                {multiplayerState.phase === 'menu' && (
                  <MultiplayerScreen
                    user={user}
                    onLobbyJoined={handleLobbyJoined}
                    onBack={exitMultiplayer}
                    onShowRanked={showRanked}
                  />
                )}
                {multiplayerState.phase === 'join' && (
                  <MultiplayerScreen
                    user={user}
                    onLobbyJoined={handleLobbyJoined}
                    onBack={exitMultiplayer}
                    autoJoinCode={multiplayerState.autoJoinCode}
                    onShowRanked={showRanked}
                  />
                )}
                {multiplayerState.phase === 'lobby' && (
                  <MultiplayerLobby
                    lobbyId={multiplayerState.lobbyId}
                    lobbyCode={multiplayerState.lobbyCode}
                    isHost={multiplayerState.isHost}
                    initialPlayers={multiplayerState.initialPlayers}
                    initialSettings={multiplayerState.initialSettings}
                    user={user}
                    onGameStart={handleMPGameStart}
                    onLeave={exitMultiplayer}
                  />
                )}
                {multiplayerState.phase === 'game' && (
                  <MultiplayerGame
                    lobbyId={multiplayerState.lobbyId}
                    initialRoundData={multiplayerState.initialRoundData}
                    previousRoundData={multiplayerState.roundData}
                    players={multiplayerState.initialPlayers}
                    user={user}
                    onRoundEnd={handleMPRoundEnd}
                    onGameEnd={handleMPGameEnd}
                  />
                )}
                {multiplayerState.phase === 'results' && (
                  <MultiplayerResults
                    lobbyId={multiplayerState.lobbyId}
                    roundData={multiplayerState.roundData}
                    maps={multiplayerState.maps}
                    isHost={multiplayerState.isHost}
                    onNextRound={handleMPNextRound}
                    onGameEnd={handleMPGameEnd}
                    pendingLeaderboard={multiplayerState.leaderboard}
                    onShowLeaderboard={handleMPShowLeaderboard}
                    onRematch={handleMPRematch}
                    user={user}
                  />
                )}
                {multiplayerState.phase === 'leaderboard' && (
                  <MultiplayerLeaderboard
                    lobbyId={multiplayerState.lobbyId}
                    leaderboard={multiplayerState.leaderboard}
                    isHost={multiplayerState.isHost}
                    onRematch={handleMPRematch}
                    onExit={exitMultiplayer}
                  />
                )}
              </>
            )}

            {currentScreen === 'ranked' && (
              <>
                {rankedState.phase === 'queue' && (
                  <MatchmakingQueue
                    user={user}
                    onMatchFound={handleRankedMatchFound}
                    onCancel={handleRankedCancel}
                  />
                )}
                {rankedState.phase === 'lobby' && (
                  <MultiplayerLobby
                    lobbyId={rankedState.lobbyId}
                    lobbyCode={rankedState.lobbyCode}
                    isHost={false}
                    initialPlayers={rankedState.initialPlayers}
                    user={user}
                    onGameStart={handleRankedGameStart}
                    onLeave={exitRanked}
                    isRanked={true}
                    rankedOpponent={rankedState.opponent}
                    rankedYou={rankedState.rankedYou}
                  />
                )}
                {rankedState.phase === 'game' && (
                  <MultiplayerGame
                    lobbyId={rankedState.lobbyId}
                    initialRoundData={rankedState.initialRoundData}
                    previousRoundData={rankedState.roundData}
                    players={rankedState.initialPlayers}
                    user={user}
                    onRoundEnd={handleRankedRoundEnd}
                    onGameEnd={handleRankedGameEnd}
                  />
                )}
                {rankedState.phase === 'results' && (
                  <MultiplayerResults
                    lobbyId={rankedState.lobbyId}
                    roundData={rankedState.roundData}
                    maps={rankedState.maps}
                    isHost={false}
                    onNextRound={handleRankedNextRound}
                    onGameEnd={handleRankedGameEnd}
                    pendingLeaderboard={rankedState.leaderboard}
                    onShowLeaderboard={handleRankedShowLeaderboard}
                    onRematch={() => {}}
                    user={user}
                  />
                )}
                {rankedState.phase === 'leaderboard' && (
                  <RankedEndScreen
                    leaderboard={rankedState.leaderboard}
                    rankedResult={rankedState.rankedResult}
                    onPlayAgain={showRanked}
                    onExit={exitRanked}
                  />
                )}
              </>
            )}

            {isAdminScreen && (
              <AdminLayout
                user={user}
                currentScreen={currentScreen}
                onNavigate={setCurrentScreen}
              >
                {currentScreen === 'admin-dashboard' && (
                  <AdminDashboardScreen
                    user={user}
                    onNavigate={setCurrentScreen}
                  />
                )}
                {currentScreen === 'admin-points' && (
                  <AdminScreen
                    user={user}
                  />
                )}
                {currentScreen === 'admin-maps' && (
                  <AdminMapsScreen
                    user={user}
                  />
                )}
                {currentScreen === 'admin-gamemodes' && (
                  <AdminGamemodesScreen
                    user={user}
                  />
                )}
                {currentScreen === 'admin-users' && (
                  <AdminUsersScreen
                    user={user}
                  />
                )}
                {currentScreen === 'admin-sounds' && (
                  <AdminSoundsScreen
                    user={user}
                  />
                )}
                {currentScreen === 'admin-roles' && (
                  <AdminRolesScreen
                    user={user}
                  />
                )}
                {currentScreen === 'admin-reports' && (
                  <AdminReportsScreen
                    user={user}
                  />
                )}
                {currentScreen === 'admin-heatmap' && (
                  <AdminHeatmapScreen
                    user={user}
                  />
                )}
                {currentScreen === 'admin-seasons' && (
                  <AdminSeasonsScreen
                    user={user}
                  />
                )}
              </AdminLayout>
            )}
          </Suspense>

          <CookieConsent />
          <Advertisement zone="footer-banner" />
          <Footer />
          <FeedbackBubble hidden={['game', 'overlay-stream'].includes(currentScreen)} user={user} />

          {/* Global lobby invite toast */}
          {pendingInvite && (
            <div className="invite-toast-global">
              <div className="invite-toast-content">
                <img
                  src={pendingInvite.from.avatarUrl || '/images/default-avatar.svg'}
                  alt=""
                  className="invite-toast-avatar"
                />
                <div className="invite-toast-text">
                  <strong>{pendingInvite.from.nickname}</strong>
                  <span>{t('multiplayer.invitedYouToPlay', 'invited you to play!')}</span>
                </div>
                <div className="invite-toast-actions">
                  <button className="invite-toast-btn accept" onClick={() => handleAcceptInvite(pendingInvite.lobbyCode)}>
                    <i className="fas fa-check"></i> {t('friends.join', 'Join')}
                  </button>
                  <button className="invite-toast-btn dismiss" onClick={handleDismissInvite}>
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              </div>
            </div>
          )}
          </div>
        </AdProvider>
      </ToastProvider>
    </ThemeProvider>
  );
}

export default App;
